/**
 * Usage-cap banner detection for the PTY harness.
 *
 * The SDK harness surfaces hard caps as structured `rate_limit_event`s, but the
 * interactive CLI only prints a banner into the conversation, e.g.
 *   "You've hit your weekly limit · resets 7am (UTC)"
 * This matcher recognizes that banner in transcript text so PtySession can
 * synthesize the same harness-level event and both harnesses share one
 * downstream cap-handling path (key cycle → pause fallback).
 */

export interface UsageLimitBannerMatch {
  rateLimitType: "seven_day" | "five_hour";
  /** Epoch SECONDS of the reset when the banner carried a parseable time —
   *  matching the SDK event's unit (see epochSecondsToISO). */
  resetsAtEpochSeconds?: number;
}

// "You've hit your weekly limit", "You've reached your session limit",
// "You have hit your usage limit", … — keep the qualifier loose; only
// "weekly" changes classification.
const BANNER_RE = /you'?(?:ve| have) (?:hit|reached) your ([\w-]+ )?(?:usage )?limit/i;

// "resets 7am (UTC)", "resets 2:30pm (UTC)" — the CLI prints wall-clock UTC
// with no date. Other timezone spellings are ignored (no reliable offset).
const RESET_RE = /resets?\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)\s*\(UTC\)/i;

/**
 * Next future occurrence of the banner's wall-clock UTC time. The banner
 * carries no date, so this is a floor: for a weekly cap the true reset may be
 * days later — under-holding just means the key re-trips and gets re-stamped.
 */
function nextUtcOccurrenceSeconds(
  hour12: number,
  minute: number,
  meridiem: string,
  nowMs: number,
): number {
  let hour = hour12 % 12;
  if (meridiem.toLowerCase() === "pm") hour += 12;
  const candidate = new Date(nowMs);
  candidate.setUTCHours(hour, minute, 0, 0);
  if (candidate.getTime() <= nowMs) candidate.setUTCDate(candidate.getUTCDate() + 1);
  return Math.floor(candidate.getTime() / 1000);
}

export function matchUsageLimitBanner(
  text: string,
  now = Date.now(),
): UsageLimitBannerMatch | null {
  const banner = BANNER_RE.exec(text);
  if (!banner) return null;
  const qualifier = banner[1]?.trim().toLowerCase();
  const rateLimitType = qualifier === "weekly" ? "seven_day" : "five_hour";

  const reset = RESET_RE.exec(text);
  return {
    rateLimitType,
    ...(reset
      ? {
          resetsAtEpochSeconds: nextUtcOccurrenceSeconds(
            Number(reset[1]),
            reset[2] ? Number(reset[2]) : 0,
            reset[3],
            now,
          ),
        }
      : {}),
  };
}
